import { PREFERENCE_DEFAULT } from "csi/constants";
import { ComponentItem } from "designSystem/types/component-item-types";
import { clamp } from "lodash";
import { action, computed, makeObservable } from "mobx";
import { ChangeEvent, FunctionComponent, SyntheticEvent } from "react";
import { ImageSize } from "src/c8-platform/type/image";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ICustomViewPropertyOptions, IPropertyViewDropDownOptions } from "./CustomViewOptions";
import CustomViewOptionsStore from "./CustomViewOptionsStore";

/**
 * This store contains information about property view
 */
class PropertyCustomViewOptionsStore extends CustomViewOptionsStore<ICustomViewPropertyOptions> {
	/** all the dropdown boxes selection options for the custom view */
	readonly propertyDropDownOptions: IPropertyViewDropDownOptions;

	constructor(
		propertyDropDownOptions: IPropertyViewDropDownOptions,
		component: FunctionComponent,
		customViewDialogStore: CustomViewDialogStore,
		options?: ICustomViewPropertyOptions,
	) {
		super(propertyDropDownOptions, component, customViewDialogStore, options);
		makeObservable(this);

		this.propertyDropDownOptions = propertyDropDownOptions;
	}

	@computed get selectedPropertyImageLabel(): ComponentItem {
		const { propertyImageLabel } = this.options;
		return this.propertyDropDownOptions.imageLabels.find((item) => item.id === (propertyImageLabel || PREFERENCE_DEFAULT));
	}

	@computed get selectedPropertyColumn(): ComponentItem {
		const { propertyColumns } = this.options;
		return this.propertyDropDownOptions.propertyColumns.find((item) => item.id === propertyColumns.toString());
	}

	@computed get selectedImageSizes(): ComponentItem {
		const { imageSize } = this.options;
		return this.propertyDropDownOptions.imageSizes.find((item) => item.id === imageSize);
	}

	/** set the value for display property image */
	@action setDisplayPropertyImage = (event: SyntheticEvent, value: boolean): void => {
		this.options.displayPropertyImage = value;
	};

	/** set the value for image label */
	@action setImageLabel = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.propertyImageLabel = value.id;
	};

	/** set the value for image size */
	@action setImageSize = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.imageSize = value.id as ImageSize;
	};

	/** validate that number is within bounds and sync up with width property */
	@action onBlurPrintImageHeight = (): void => {
		const { getAllowedPrintImageHeight, getAllowedPrintImageWidth, options } = this;
		let { propertyImageHeight } = options;
		
		if (propertyImageHeight) {
			propertyImageHeight = options.propertyImageHeight = getAllowedPrintImageHeight(propertyImageHeight);
		}

		if (propertyImageHeight) {
			options.propertyImageWidth ||= getAllowedPrintImageWidth(propertyImageHeight);
		} else {
			options.propertyImageWidth = undefined;
		}
	};
	
	/** set the value for height of image when printing */
	@action onBlurPrintImageWidth = (): void => {
		const { getAllowedPrintImageHeight, getAllowedPrintImageWidth, options } = this;
		let { propertyImageWidth } = options;
		
		if (propertyImageWidth) {
			propertyImageWidth = options.propertyImageWidth = getAllowedPrintImageWidth(propertyImageWidth);
		}

		if (propertyImageWidth) {
			options.propertyImageHeight ||= getAllowedPrintImageHeight(propertyImageWidth);
		} else {
			options.propertyImageHeight = undefined;
		}
	};
	
	/** set the value for height of image when printing */
	@action setPrintImageHeight = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		// TODO: when moving to new design system, switch to using number input with min/max values
		this.options.propertyImageHeight = Number(event.target.value) || undefined;
	};
	
	/** set the value for width of image when printing */
	@action setPrintImageWidth = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		// TODO: when moving to new design system, switch to using number input with min/max values
		this.options.propertyImageWidth = Number(event.target.value) || undefined;
	};

	/** set the value for property columns */
	@action setPropertyColumns = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.propertyColumns = Number(value.id);
	};

	/** set the value for show thumbnail label */
	@action setShowThumbnailLabel = (event: SyntheticEvent, value: boolean): void => {
		this.options.propertyShowThumbnailLabel = value;
	};

	/** return allowed height for print image */
	private getAllowedPrintImageHeight = (value: number | undefined): number | undefined => {
		return value ? clamp(Number(value), 32, this.propertyDropDownOptions.maxPrintImageHeight) : value;
	};

	/** return allowed width for print image */
	private getAllowedPrintImageWidth = (value: number | undefined): number | undefined => {
		return value ? clamp(Number(value), 32, this.propertyDropDownOptions.maxPrintImageWidth) : value;
	};
}

export = PropertyCustomViewOptionsStore;